import { EyeOff } from 'lucide-react'

import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'
import {
  ManualQuestionnaire,
  Questionnaire,
  RiskQuestionnaire,
} from '@/gen/questionnaire/v2/model_pb'

import { useQuestionnaireStatusLabel } from '@/lib/assessment/questionnaire-status-label.hook'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { SmartEvidenceRequestStatusBadge } from '@/components/shared/smart-evidence-request-status-badge/smart-evidence-request-status-badge'

type QuestionnaireStatusBadgeProps = {
  questionnaire: Questionnaire
}

export const QuestionnaireStatusBadge = ({ questionnaire }: QuestionnaireStatusBadgeProps) => {
  switch (questionnaire?.questionnaire.case) {
    case 'manualQuestionnaire':
      return (
        <ManualQuestionnaireStatusBadge
          manualQuestionnaire={questionnaire.questionnaire.value}
          questionnaireStatus={questionnaire.base?.status ?? QuestionnaireStatus.UNSPECIFIED}
        />
      )
    case 'riskQuestionnaire':
      return (
        <RiskQuestionnaireStatusBadge
          riskQuestionnaire={questionnaire.questionnaire.value}
          questionnaireStatus={questionnaire.base?.status ?? QuestionnaireStatus.UNSPECIFIED}
        />
      )
    default:
      return null
  }
}

type ManualQuestionnaireStatusBadgeProps = {
  manualQuestionnaire: ManualQuestionnaire
  questionnaireStatus: QuestionnaireStatus
}

const ManualQuestionnaireStatusBadge = ({
  manualQuestionnaire,
  questionnaireStatus,
}: ManualQuestionnaireStatusBadgeProps) => {
  const { totalAnswered, totalQuestions } = manualQuestionnaire ?? {
    totalAnswered: 0,
    totalQuestions: 0,
  }

  const { label, variant } = useQuestionnaireStatusLabel({
    status: questionnaireStatus,
    answeredQuestions: totalAnswered,
    totalQuestions,
  })

  return (
    <Badge className='text-nowrap' variant={variant}>
      {label}
    </Badge>
  )
}

type RiskQuestionnaireStatusBadgeProps = {
  riskQuestionnaire: RiskQuestionnaire
  questionnaireStatus: QuestionnaireStatus
}

const RiskQuestionnaireStatusBadge = ({
  riskQuestionnaire,
  questionnaireStatus,
}: RiskQuestionnaireStatusBadgeProps) => {
  const { totalAwaitingRecipient } = riskQuestionnaire

  if (questionnaireStatus === QuestionnaireStatus.SUBMITTED) {
    return <SmartEvidenceRequestStatusBadge status={questionnaireStatus} />
  }

  return (
    <Badge variant='promotion' className='text-nowrap'>
      {totalAwaitingRecipient} Waiting for recipient
    </Badge>
  )
}

export const NoRecipientsBadge = () => {
  return (
    <Badge variant='danger'>
      <TextWithIcon text='No recipients' icon={<EyeOff className='size-3.5' />} />
    </Badge>
  )
}
